/**
 * Import History Page
 * Knowledge Management UI for viewing and managing import sessions and learned patterns
 */

import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import {
  History,
  Brain,
  FileText,
  CheckCircle2,
  Clock,
  XCircle,
  MoreVertical,
  Save,
  Trash2,
  Eye,
  TrendingUp,
  Settings2,
  RefreshCw,
  Calendar,
  Building2,
  Download,
} from "lucide-react";

// Status badge component
function StatusBadge({ status }: { status: string }) {
  const variants: Record<
    string,
    {
      variant: "default" | "secondary" | "destructive" | "outline";
      icon: React.ReactNode;
    }
  > = {
    completed: {
      variant: "default",
      icon: <CheckCircle2 className="h-3 w-3" />,
    },
    awaiting_review: {
      variant: "secondary",
      icon: <Clock className="h-3 w-3" />,
    },
    analyzing: {
      variant: "outline",
      icon: <RefreshCw className="h-3 w-3 animate-spin" />,
    },
    failed: { variant: "destructive", icon: <XCircle className="h-3 w-3" /> },
  };

  const config = variants[status] || variants.awaiting_review;

  return (
    <Badge variant={config.variant} className="flex items-center gap-1">
      {config.icon}
      <span className="capitalize">{status.replace(/_/g, " ")}</span>
    </Badge>
  );
}

// Learning decision badge
function LearningBadge({ decision }: { decision: string | null }) {
  if (!decision) return <Badge variant="outline">Pending</Badge>;

  const variants: Record<
    string,
    { variant: "default" | "secondary" | "destructive"; label: string }
  > = {
    saved: { variant: "default", label: "Saved" },
    deferred: { variant: "secondary", label: "Deferred" },
    declined: { variant: "destructive", label: "Declined" },
  };

  const config = variants[decision] || {
    variant: "secondary",
    label: decision,
  };

  return <Badge variant={config.variant}>{config.label}</Badge>;
}

export default function ImportHistory() {
  const { selectedCompanyId } = useCompany();
  const [selectedSession, setSelectedSession] = useState<any | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [userNotes, setUserNotes] = useState("");

  // Fetch import sessions from Intelligent Import System
  const {
    data: sessions,
    isLoading: loadingSessions,
    refetch: refetchSessions,
  } = trpc.learning.getImportSessions.useQuery(
    { companyId: selectedCompanyId! },
    { enabled: !!selectedCompanyId }
  );

  // Fetch learned patterns
  const {
    data: patterns,
    isLoading: loadingPatterns,
    refetch: refetchPatterns,
  } = trpc.learning.getCompanyLearnings.useQuery(
    { companyId: selectedCompanyId! },
    { enabled: !!selectedCompanyId }
  );

  // Save deferred learning mutation
  const saveLearning = trpc.enhancedImport.saveImportLearning.useMutation({
    onSuccess: () => {
      toast.success("Learning saved successfully");
      setShowSaveDialog(false);
      refetchSessions();
      refetchPatterns();
    },
    onError: error => {
      toast.error(`Failed to save: ${error.message}`);
    },
  });

  // Delete document learning pattern mutation
  const deletePattern = trpc.learning.deleteDocumentLearning.useMutation({
    onSuccess: () => {
      toast.success("Pattern deleted");
      refetchPatterns();
    },
    onError: error => {
      toast.error(`Failed to delete: ${error.message}`);
    },
  });

  const handleViewDetails = (session: any) => {
    setSelectedSession(session);
    setShowDetailsDialog(true);
  };

  const handleSaveDeferred = (session: any) => {
    setSelectedSession(session);
    setUserNotes(session.userNotes || "");
    setShowSaveDialog(true);
  };

  const handleConfirmSave = async () => {
    if (!selectedSession || !selectedCompanyId) return;

    await saveLearning.mutateAsync({
      companyId: selectedCompanyId,
      sessionId: selectedSession.id,
      decision: "saved",
      formatLearning: {
        documentType: selectedSession.documentType || "unknown",
        delimiter: selectedSession.delimiter || ",",
        encoding: selectedSession.encoding,
        dateFormat: selectedSession.dateFormat,
      },
      importResults: {
        transactionsImported: selectedSession.transactionsImported || 0,
        transactionsSkipped: selectedSession.transactionsSkipped || 0,
      },
      userNotes: userNotes.trim() || undefined,
    });
  };

  const handleDeletePattern = async (patternId: number) => {
    if (!selectedCompanyId) return;
    await deletePattern.mutateAsync({
      companyId: selectedCompanyId,
      patternId,
    });
  };

  if (!selectedCompanyId) {
    return (
      <DashboardLayout>
        <Card>
          <CardHeader>
            <CardTitle>No Company Selected</CardTitle>
            <CardDescription>
              Please select a company to view import history
            </CardDescription>
          </CardHeader>
        </Card>
      </DashboardLayout>
    );
  }

  // Filter deferred sessions (awaiting review)
  const deferredSessions =
    sessions?.filter(s => s.status === "awaiting_review") || [];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <History className="h-8 w-8" />
              Import History
            </h1>
            <p className="text-muted-foreground mt-1">
              View past imports and manage learned patterns for faster future
              imports
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => (window.location.href = "/bank-import")}
          >
            <Download className="h-4 w-4 mr-2" />
            New Import
          </Button>
        </div>

        {/* Deferred Learning Alert */}
        {deferredSessions.length > 0 && (
          <Card className="border-[var(--warning)] bg-warning/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-warning">
                <Clock className="h-5 w-5" />
                Pending Learnings ({deferredSessions.length})
              </CardTitle>
              <CardDescription>
                You have imports with deferred learning. Save them to improve
                future imports.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {deferredSessions.slice(0, 5).map((session: any) => (
                  <Button
                    key={session.id}
                    variant="outline"
                    size="sm"
                    onClick={() => handleSaveDeferred(session)}
                  >
                    <Save className="h-3 w-3 mr-1" />
                    {session.documentType || "Import"} -{" "}
                    {new Date(session.createdAt).toLocaleDateString("de-CH")}
                  </Button>
                ))}
                {deferredSessions.length > 5 && (
                  <Badge variant="secondary">
                    +{deferredSessions.length - 5} more
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs for Sessions and Patterns */}
        <Tabs defaultValue="sessions" className="space-y-4">
          <TabsList>
            <TabsTrigger value="sessions" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Import Sessions
            </TabsTrigger>
            <TabsTrigger value="patterns" className="flex items-center gap-2">
              <Brain className="h-4 w-4" />
              Learned Patterns
            </TabsTrigger>
          </TabsList>

          {/* Sessions Tab */}
          <TabsContent value="sessions">
            <Card>
              <CardHeader>
                <CardTitle>Import Sessions</CardTitle>
                <CardDescription>
                  History of all bank statement imports for this company
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingSessions ? (
                  <div className="py-12 text-center text-muted-foreground">
                    <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
                    Loading import history...
                  </div>
                ) : sessions && sessions.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Format</TableHead>
                        <TableHead>Transactions</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Learning</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sessions.map((session: any) => (
                        <TableRow key={session.id}>
                          <TableCell className="font-mono text-sm">
                            {new Date(session.createdAt).toLocaleDateString(
                              "de-CH"
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-muted-foreground" />
                              <span>{session.documentType || "Unknown"}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="text-success font-medium">
                                {session.transactionsImported || 0}
                              </span>
                              {(session.transactionsSkipped || 0) > 0 && (
                                <span className="text-muted-foreground text-xs">
                                  (+{session.transactionsSkipped} skipped)
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={session.status} />
                          </TableCell>
                          <TableCell>
                            <LearningBadge
                              decision={session.learningDecision}
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label="Session actions"
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() => handleViewDetails(session)}
                                >
                                  <Eye className="h-4 w-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                {session.status === "awaiting_review" && (
                                  <DropdownMenuItem
                                    onClick={() => handleSaveDeferred(session)}
                                  >
                                    <Save className="h-4 w-4 mr-2" />
                                    Save Learning
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="py-12 text-center text-muted-foreground">
                    <History className="h-12 w-12 mx-auto mb-4 opacity-20" />
                    <p>No import sessions yet</p>
                    <Button
                      variant="link"
                      onClick={() => (window.location.href = "/bank-import")}
                    >
                      Import your first bank statement
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Patterns Tab */}
          <TabsContent value="patterns">
            <Card>
              <CardHeader>
                <CardTitle>Learned Patterns</CardTitle>
                <CardDescription>
                  Format patterns the system has learned from your imports
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingPatterns ? (
                  <div className="py-12 text-center text-muted-foreground">
                    <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
                    Loading patterns...
                  </div>
                ) : patterns && patterns.length > 0 ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    {patterns.map((pattern: any) => (
                      <Card key={pattern.id} className="relative">
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between">
                            <CardTitle className="text-base flex items-center gap-2">
                              <Brain className="h-4 w-4" />
                              {pattern.documentType || "Unknown Format"}
                            </CardTitle>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  aria-label="Pattern actions"
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() =>
                                    handleDeletePattern(pattern.id)
                                  }
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete Pattern
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">
                                Times Used
                              </span>
                              <Badge variant="outline">
                                {pattern.timesEncountered}x
                              </Badge>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">
                                Success Rate
                              </span>
                              <Badge
                                variant={
                                  pattern.successRate >= 80
                                    ? "default"
                                    : "secondary"
                                }
                              >
                                {pattern.successRate}%
                              </Badge>
                            </div>
                            {pattern.delimiter && (
                              <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">
                                  Delimiter
                                </span>
                                <span className="font-mono">
                                  {pattern.delimiter === ";"
                                    ? "Semicolon"
                                    : "Comma"}
                                </span>
                              </div>
                            )}
                            {pattern.dateFormat && (
                              <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">
                                  Date Format
                                </span>
                                <span className="font-mono">
                                  {pattern.dateFormat}
                                </span>
                              </div>
                            )}
                            <div className="pt-2 border-t text-xs text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                Last used:{" "}
                                {new Date(
                                  pattern.lastEncounteredAt
                                ).toLocaleDateString("de-CH")}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="py-12 text-center text-muted-foreground">
                    <Brain className="h-12 w-12 mx-auto mb-4 opacity-20" />
                    <p>No learned patterns yet</p>
                    <p className="text-sm mt-1">
                      Import bank statements and save the learning to build
                      patterns
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Session Details Dialog */}
        <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Import Session Details
              </DialogTitle>
            </DialogHeader>
            {selectedSession && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Date</Label>
                    <p className="font-medium">
                      {new Date(selectedSession.createdAt).toLocaleDateString(
                        "de-CH",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        }
                      )}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Format</Label>
                    <p className="font-medium">
                      {selectedSession.documentType || "Unknown"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">
                      Transactions Imported
                    </Label>
                    <p className="font-medium text-success">
                      {selectedSession.transactionsImported || 0}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">
                      Transactions Skipped
                    </Label>
                    <p className="font-medium">
                      {selectedSession.transactionsSkipped || 0}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Status</Label>
                    <div className="mt-1">
                      <StatusBadge status={selectedSession.status} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Learning</Label>
                    <div className="mt-1">
                      <LearningBadge
                        decision={selectedSession.learningDecision}
                      />
                    </div>
                  </div>
                </div>
                {selectedSession.userNotes && (
                  <div>
                    <Label className="text-muted-foreground">Notes</Label>
                    <p className="text-sm mt-1">{selectedSession.userNotes}</p>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowDetailsDialog(false)}
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Save Deferred Learning Dialog */}
        <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Save className="h-5 w-5" />
                Save Learning
              </DialogTitle>
              <DialogDescription>
                Save this import's settings for faster future imports of the
                same format.
              </DialogDescription>
            </DialogHeader>
            {selectedSession && (
              <div className="space-y-4">
                <Card>
                  <CardContent className="p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Format</span>
                      <span className="font-medium">
                        {selectedSession.documentType || "Unknown"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Imported</span>
                      <span className="font-medium text-success">
                        {selectedSession.transactionsImported || 0} transactions
                      </span>
                    </div>
                  </CardContent>
                </Card>
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (optional)</Label>
                  <Textarea
                    id="notes"
                    placeholder="Add any notes about this format..."
                    value={userNotes}
                    onChange={e => setUserNotes(e.target.value)}
                    rows={2}
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowSaveDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmSave}
                disabled={saveLearning.isPending}
              >
                {saveLearning.isPending ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Learning
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
